import React, {memo, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";
import {Image} from "../../../components/image";
import {toUriSegment} from "../../../utils/utils";

const PLACEHOLDER = 'https://dr1k2g3wmnols.cloudfront.net/images/image-placeholder.png';
const MAX_CARD_IMAGES = 5;
const FIRST_IMAGE_ROOT_MARGIN = '480px 0px';

function Chevron({direction}) {
    return (
        <svg
            aria-hidden="true"
            className={classNames(
                "h-[18px] w-[18px] transition-transform duration-200 ease-out group-hover:scale-[1.05]",
                direction === 'right' ? "translate-x-[0.5px]" : "-translate-x-[0.5px] rotate-180"
            )}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7 4.5 13.5 10 7 15.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export const ProductCardMediaCarousel = memo(({
    product,
    id,
    href,
    aspectRatioClassName,
    overlay,
    imageWidth = 480,
    imageQuality = 70
}) => {
    const rootRef = useRef(null);
    const trackRef = useRef(null);
    const slideRefs = useRef([]);
    const movedDuringDragRef = useRef(false);
    const pointerStateRef = useRef(null);
    const programmaticTargetIndexRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [loadedImageIndexes, setLoadedImageIndexes] = useState(() => new Set([0]));
    const [isInLoadRange, setIsInLoadRange] = useState(false);

    const images = useMemo(() => {
        const productImages = Array.isArray(product?.images) ? product.images : [];
        const imagesState = Array.isArray(product?.imagesState) ? product.imagesState : [];

        if (!productImages.length) {
            return [{src: PLACEHOLDER, key: 'placeholder'}];
        }

        return productImages.map((image, index) => ({
            src: imagesState[index]?.__cx__?.key || image || PLACEHOLDER,
            key: imagesState[index]?.__cx__?.id || image || `image-${index}`
        }));
    }, [product?.images, product?.imagesState]);

    const totalImages = images.length;
    const visibleImages = useMemo(() => images.slice(0, MAX_CARD_IMAGES), [images]);
    const hasMultipleImages = visibleImages.length > 1;
    const hasMoreImages = totalImages > visibleImages.length;
    const lastVisibleIndex = visibleImages.length - 1;
    const showViewMore = hasMoreImages && activeIndex === lastVisibleIndex;
    const remainingImagesCount = Math.max(totalImages - MAX_CARD_IMAGES, 0);
    const paginationOffsetClassName = overlay ? "bottom-4" : "bottom-3";
    const overlayButtonRoundnessStyle = {borderRadius: "var(--button-roundness, 999px)"};
    const carouselId = useMemo(() => {
        const rawId = [product?.uuid, product?.stockNumber, product?.title, id].filter(Boolean).join('-');
        return `product-card-carousel-${toUriSegment(String(rawId || 'item'))}`;
    }, [id, product?.stockNumber, product?.title, product?.uuid]);

    const loadImageRange = (index, includeLookahead = true) => {
        const boundedIndex = Math.max(0, Math.min(index, visibleImages.length - 1));
        const nextIndex = Math.min(boundedIndex + 1, visibleImages.length - 1);

        setLoadedImageIndexes((current) => {
            if (current.has(boundedIndex) && (!includeLookahead || current.has(nextIndex))) return current;

            const next = new Set(current);
            next.add(boundedIndex);
            if (includeLookahead) next.add(nextIndex);
            return next;
        });
    };

    const scrollToIndex = (index) => {
        const boundedIndex = Math.max(0, Math.min(index, visibleImages.length - 1));
        loadImageRange(boundedIndex);

        const track = trackRef.current;
        if (!track) return;

        const targetSlide = slideRefs.current[boundedIndex];
        programmaticTargetIndexRef.current = boundedIndex;
        track.scrollTo({
            left: targetSlide?.offsetLeft || 0,
            behavior: 'smooth'
        });
        setActiveIndex(boundedIndex);
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        const handleScroll = () => {
            const pendingIndex = programmaticTargetIndexRef.current;
            if (pendingIndex !== null) {
                const pendingSlide = slideRefs.current[pendingIndex];
                const pendingOffset = pendingSlide?.offsetLeft || 0;

                if (Math.abs(track.scrollLeft - pendingOffset) <= 2) {
                    programmaticTargetIndexRef.current = null;
                } else {
                    return;
                }
            }

            const nextIndex = slideRefs.current.reduce((closestIndex, slide, index) => {
                if (!slide) return closestIndex;

                const currentDistance = Math.abs((slideRefs.current[closestIndex]?.offsetLeft || 0) - track.scrollLeft);
                const nextDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
                return nextDistance < currentDistance ? index : closestIndex;
            }, 0);
            setActiveIndex((prev) => prev === nextIndex ? prev : nextIndex);
        };

        handleScroll();
        track.addEventListener('scroll', handleScroll, {passive: true});
        return () => track.removeEventListener('scroll', handleScroll);
    }, [visibleImages.length]);

    useEffect(() => {
        setActiveIndex((currentIndex) => Math.min(currentIndex, Math.max(visibleImages.length - 1, 0)));
    }, [visibleImages.length]);

    useEffect(() => {
        setLoadedImageIndexes(new Set([0]));
        setIsInLoadRange(false);
    }, [product?.uuid, product?.stockNumber, visibleImages[0]?.key]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        if (!('IntersectionObserver' in window)) {
            setIsInLoadRange(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting) return;
            setIsInLoadRange(true);
            observer.disconnect();
        }, {
            rootMargin: FIRST_IMAGE_ROOT_MARGIN,
            threshold: 0.1
        });

        observer.observe(root);
        return () => observer.disconnect();
    }, [product?.uuid, product?.stockNumber, visibleImages[0]?.key]);

    useEffect(() => {
        if (!isInLoadRange) return;
        loadImageRange(activeIndex, true);
    }, [activeIndex, isInLoadRange, visibleImages.length]);

    const handlePointerDown = (event) => {
        if (!hasMultipleImages) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button') || event.target.closest('[data-product-card-carousel-view-more]')) return;

        loadImageRange(activeIndex + 1, false);
        programmaticTargetIndexRef.current = null;
        pointerStateRef.current = {
            pointerId: event.pointerId,
            pointerType: event.pointerType,
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: trackRef.current?.scrollLeft || 0,
            isDragging: false
        };
        movedDuringDragRef.current = false;
    };

    const handlePointerMove = (event) => {
        const pointerState = pointerStateRef.current;
        const track = trackRef.current;
        if (!pointerState || !track) return;

        const deltaX = event.clientX - pointerState.startX;
        const deltaY = event.clientY - pointerState.startY;
        const hasMovedEnough = Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8;

        if (hasMovedEnough) {
            movedDuringDragRef.current = true;
        }

        if (pointerState.pointerType !== 'mouse') return;
        if (!pointerState.isDragging && Math.abs(deltaX) <= 8) return;

        if (!pointerState.isDragging) {
            pointerState.isDragging = true;
            setIsDragging(true);
            rootRef.current?.setPointerCapture?.(event.pointerId);
        }

        event.preventDefault();
        track.scrollLeft = pointerState.startScrollLeft - deltaX;
    };

    const clearPointerState = () => {
        pointerStateRef.current = null;
        window.setTimeout(() => {
            movedDuringDragRef.current = false;
        }, 0);
        setIsDragging(false);
    };

    const handlePointerUp = (event) => {
        if (pointerStateRef.current?.pointerId !== event.pointerId) return;
        if (pointerStateRef.current?.pointerType === 'mouse' && pointerStateRef.current?.isDragging) {
            rootRef.current?.releasePointerCapture?.(event.pointerId);
        }
        clearPointerState();
    };

    const handleLinkClickCapture = (event) => {
        if (!movedDuringDragRef.current) return;
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <div
            ref={rootRef}
            data-product-card-carousel-root=""
            className={classNames(aspectRatioClassName, "group isolate relative z-0 overflow-hidden bg-gray-100")}
            onMouseEnter={() => {
                if (hasMultipleImages) loadImageRange(activeIndex + 1, false);
            }}
            onFocus={() => {
                if (hasMultipleImages) loadImageRange(activeIndex + 1, false);
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={clearPointerState}
            onPointerLeave={(event) => {
                if (pointerStateRef.current?.pointerType === 'mouse' && event.buttons === 0) {
                    clearPointerState();
                }
            }}
        >
            <style>
                {`
                    [data-product-card-carousel-arrow] {
                        display: none;
                        opacity: 0;
                        pointer-events: none;
                    }

                    [data-product-card-carousel-view-more] {
                        display: inline-flex;
                        opacity: 0;
                        pointer-events: none;
                    }

                    [data-product-card-carousel-view-more][data-enabled="true"] {
                        opacity: 1;
                        pointer-events: auto;
                    }

                    @media (min-width: 768px) {
                        [data-product-card-carousel-arrow] {
                            display: flex;
                        }

                        [data-product-card-carousel-view-more] {
                            display: inline-flex;
                        }

                        [data-product-card-carousel-arrow][data-enabled="true"] {
                            opacity: 0;
                        }

                        [data-product-card-carousel-view-more][data-enabled="true"] {
                            opacity: 0;
                        }

                        [data-product-card-carousel-root]:hover [data-product-card-carousel-arrow][data-enabled="true"],
                        [data-product-card-carousel-root]:focus-within [data-product-card-carousel-arrow][data-enabled="true"],
                        [data-product-card-carousel-root]:hover [data-product-card-carousel-view-more][data-enabled="true"],
                        [data-product-card-carousel-root]:focus-within [data-product-card-carousel-view-more][data-enabled="true"] {
                            opacity: 1;
                            pointer-events: auto;
                        }
                    }
                `}
            </style>
            <div
                ref={trackRef}
                data-product-card-carousel-track=""
                className={classNames(
                    "absolute inset-0 z-0 flex h-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
                    hasMultipleImages ? "cursor-grab" : null,
                    isDragging ? "cursor-grabbing select-none" : null
                )}
                style={{
                    scrollBehavior: isDragging ? 'auto' : 'smooth',
                    touchAction: 'pan-x pan-y pinch-zoom',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {visibleImages.map((image, index) => (
                    <a
                        id={`${carouselId}-${index}`}
                        key={image.key}
                        ref={(element) => {
                            slideRefs.current[index] = element;
                        }}
                        data-action=""
                        data-product-card-carousel-link=""
                        href={href}
                        aria-label={product?.title || 'View product'}
                        className="relative h-full w-full flex-none snap-start snap-always overflow-hidden"
                        draggable={false}
                        onDragStart={(event) => {
                            event.preventDefault();
                        }}
                        onClickCapture={handleLinkClickCapture}
                    >
                        {loadedImageIndexes.has(index) ? (
                            <Image
                                src={image.src}
                                alt={product?.title}
                                width={imageWidth}
                                quality={imageQuality}
                                lazy={index === 0 ? null : 'lazy'}
                                rootMargin={index === 0 ? FIRST_IMAGE_ROOT_MARGIN : '0px'}
                                forceLoad={isInLoadRange}
                                disableLoadAnimation={true}
                                className="pointer-events-none h-full w-full object-cover object-center"
                            />
                        ) : (
                            <div className="h-full w-full bg-gray-100" aria-hidden="true"/>
                        )}
                    </a>
                ))}
            </div>

            {overlay ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-2">
                    {overlay}
                </div>
            ) : null}

            {hasMultipleImages ? (
                <>
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center pl-3">
                        <button
                            type="button"
                            data-product-card-carousel-arrow=""
                            data-enabled={activeIndex > 0 ? 'true' : 'false'}
                            aria-label="Previous image"
                            aria-disabled={activeIndex === 0}
                            tabIndex={activeIndex === 0 ? -1 : 0}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (activeIndex === 0) return;
                                loadImageRange(activeIndex - 1);
                                scrollToIndex(activeIndex - 1);
                            }}
                            className="group pointer-events-auto flex h-8 w-8 items-center justify-center border border-slate-900/10 bg-white/90 text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,0.12)] backdrop-blur-sm transition duration-200 ease-out hover:bg-white hover:text-slate-900"
                            style={overlayButtonRoundnessStyle}
                        >
                            <Chevron direction="left"/>
                        </button>
                    </div>

                    <div className="pointer-events-none absolute inset-y-0 right-0 z-20 flex items-center pr-3">
                        <div className="relative flex h-8 w-[5.25rem] items-center justify-end">
                            <a
                                data-action=""
                                data-product-card-carousel-view-more=""
                                data-enabled={showViewMore ? 'true' : 'false'}
                                href={href}
                                className={classNames(
                                    "pointer-events-auto absolute right-0 inline-flex items-center whitespace-nowrap border border-slate-900/10 bg-white/90 px-2.5 py-1.5 text-[11px] font-semibold leading-none !text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.12)] backdrop-blur-sm transition-all duration-200 ease-out hover:bg-white",
                                    showViewMore ? "translate-x-0" : "opacity-0 translate-x-1 pointer-events-none"
                                )}
                                style={overlayButtonRoundnessStyle}
                                onClickCapture={handleLinkClickCapture}>
                                {`${remainingImagesCount} more`}
                            </a>
                            <button
                                type="button"
                                data-product-card-carousel-arrow=""
                                data-enabled={showViewMore ? 'false' : activeIndex < visibleImages.length - 1 ? 'true' : 'false'}
                                aria-label="Next image"
                                aria-disabled={activeIndex === visibleImages.length - 1}
                                tabIndex={activeIndex === visibleImages.length - 1 ? -1 : 0}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (activeIndex === visibleImages.length - 1) return;
                                    loadImageRange(activeIndex + 1);
                                    scrollToIndex(activeIndex + 1);
                                }}
                                className={classNames(
                                    "group pointer-events-auto absolute right-0 flex h-8 w-8 items-center justify-center border border-slate-900/10 bg-white/90 text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,0.12)] backdrop-blur-sm transition-all duration-200 ease-out hover:bg-white hover:text-slate-900",
                                    showViewMore ? "opacity-0 -translate-x-1 pointer-events-none" : "opacity-100 translate-x-0"
                                )}
                                style={overlayButtonRoundnessStyle}
                            >
                                <Chevron direction="right"/>
                            </button>
                        </div>
                    </div>

                    <div className={classNames("absolute inset-x-0 z-20 flex justify-center px-4", paginationOffsetClassName)}>
                        <div className="flex h-1 items-center">
                            {visibleImages.map((image, index) => (
                                <button
                                    key={`${image.key}-dot`}
                                    type="button"
                                    aria-label={`Go to image ${index + 1}`}
                                    aria-current={activeIndex === index ? 'true' : undefined}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        loadImageRange(index);
                                        scrollToIndex(index);
                                    }}
                                    className="flex h-3 w-3.5 items-center justify-center border-0 bg-transparent p-0"
                                >
                                    <span
                                        aria-hidden="true"
                                        className={classNames(
                                            "h-1 w-2 rounded transition-[transform,background-color,box-shadow] duration-200 ease-out will-change-transform",
                                            activeIndex === index
                                                ? "scale-[1.25] bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.10),0_0.5px_1px_rgba(15,23,42,0.05)]"
                                                : "bg-white/75 shadow-[0_0_0_1px_rgba(15,23,42,0.07),0_0.5px_0.5px_rgba(15,23,42,0.03)]"
                                        )}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                </>
            ) : null}
        </div>
    );
});

export default ProductCardMediaCarousel;
